import { profileUploadDocumentsFromDashboardRow } from './educator-profile-documents.util';

export interface DuplicateLookupOption {
  key: string;
  value: string;
}

/** Soft-deleted or flagged inactive — excluded from duplicate scope. */
export function isProfileUploadRemovedFromEducator(doc: Record<string, unknown>): boolean {
  const ia = doc['is_active'];
  if (ia === false || ia === 0 || String(ia).toLowerCase() === 'false') {
    return true;
  }
  const st = String(doc['status'] ?? doc['document_status'] ?? '')
    .toLowerCase()
    .trim();
  return st === 'deleted';
}

/** Archived / inactive status — not counted as active for duplicate check. */
export function isProfileUploadArchived(doc: Record<string, unknown>): boolean {
  const st = String(doc['status'] ?? doc['document_status'] ?? '')
    .toLowerCase()
    .trim();
  if (st === 'archived' || st === 'inactive') {
    return true;
  }
  const ia = doc['is_archived'];
  return ia === true || ia === 1 || String(ia).toLowerCase() === 'true';
}

export function isActiveProfileUploadDocument(doc: Record<string, unknown>): boolean {
  if (isProfileUploadRemovedFromEducator(doc)) {
    return false;
  }
  return !isProfileUploadArchived(doc);
}

function normalizeToken(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function normalizeFileName(value: unknown): string {
  return normalizeToken(value);
}

function findLookupMatch(
  raw: unknown,
  lookups: DuplicateLookupOption[] | undefined
): DuplicateLookupOption | undefined {
  if (!lookups?.length) {
    return undefined;
  }
  const token = normalizeToken(raw);
  if (!token) {
    return undefined;
  }
  return lookups.find(
    (opt) => normalizeToken(opt.key) === token || normalizeToken(opt.value) === token
  );
}

/** Resolve lookup key or label to canonical lowercase lookup key. */
export function normalizeCategoryForDuplicate(
  raw: unknown,
  lookups?: DuplicateLookupOption[]
): string {
  if (!raw) {
    return '';
  }
  const match = findLookupMatch(raw, lookups);
  if (match?.key) {
    return normalizeToken(match.key);
  }
  return normalizeToken(raw);
}

/** Resolve lookup key or label to canonical lowercase lookup key. */
export function normalizeTypeForDuplicate(
  raw: unknown,
  lookups?: DuplicateLookupOption[]
): string {
  if (!raw) {
    return '';
  }
  const match = findLookupMatch(raw, lookups);
  if (match?.key) {
    return normalizeToken(match.key);
  }
  return normalizeToken(raw);
}

function categoryRawValues(doc: Record<string, unknown>): unknown[] {
  return [
    doc['cd_document_category'],
    doc['document_category'],
    doc['category_id'],
    doc['category']
  ];
}

function typeRawValues(doc: Record<string, unknown>): unknown[] {
  return [doc['cd_document_type'], doc['document_type'], doc['type']];
}

function resolveDocCategoryKeys(
  doc: Record<string, unknown>,
  categoryLookups?: DuplicateLookupOption[]
): Set<string> {
  const keys = new Set<string>();
  for (const raw of categoryRawValues(doc)) {
    const canonical = normalizeCategoryForDuplicate(raw, categoryLookups);
    if (canonical) {
      keys.add(canonical);
    }
  }
  return keys;
}

function resolveDocTypeKeys(
  doc: Record<string, unknown>,
  typeLookups?: DuplicateLookupOption[]
): Set<string> {
  const keys = new Set<string>();
  for (const raw of typeRawValues(doc)) {
    const canonical = normalizeTypeForDuplicate(raw, typeLookups);
    if (canonical) {
      keys.add(canonical);
    }
  }
  return keys;
}

function profileDocFileName(doc: Record<string, unknown>): string {
  return normalizeFileName(doc['file_name'] ?? doc['name']);
}

/** Active profile uploads for an educator from dashboard SP output `p_profile_upload`. */
export function getActiveProfileUploadDocuments(
  row: Record<string, unknown> | null | undefined
): Record<string, unknown>[] {
  return profileUploadDocumentsFromDashboardRow(row).filter(isActiveProfileUploadDocument);
}

export interface FindDuplicateProfileUploadParams {
  categoryKey: string;
  typeKey?: string;
  fileName: string;
  categoryLookups?: DuplicateLookupOption[];
  typeLookups?: DuplicateLookupOption[];
}

/**
 * Match category + type (when either side has a type) + filename across active profile documents.
 * Returns the first matching row, or null.
 */
export function findDuplicateProfileUpload(
  docs: Record<string, unknown>[],
  params: FindDuplicateProfileUploadParams
): Record<string, unknown> | null {
  const fileName = normalizeFileName(params.fileName);
  if (!fileName) {
    return null;
  }
  const incomingCategory = normalizeCategoryForDuplicate(
    params.categoryKey,
    params.categoryLookups
  );
  if (!incomingCategory) {
    return null;
  }
  const incomingType = normalizeTypeForDuplicate(params.typeKey, params.typeLookups);

  for (const existing of docs) {
    if (!isActiveProfileUploadDocument(existing)) {
      continue;
    }
    const existingName = profileDocFileName(existing);
    if (!existingName || existingName !== fileName) {
      continue;
    }
    const existingCategories = resolveDocCategoryKeys(existing, params.categoryLookups);
    if (!existingCategories.has(incomingCategory)) {
      continue;
    }
    const existingTypes = resolveDocTypeKeys(existing, params.typeLookups);
    const hasAnyDocType = !!(incomingType || existingTypes.size > 0);
    if (!hasAnyDocType) {
      return existing;
    }
    if (incomingType && existingTypes.has(incomingType)) {
      return existing;
    }
  }
  return null;
}

export function toDuplicateLookupOptions(
  options: Array<{ key: string; value: string }> | undefined | null
): DuplicateLookupOption[] {
  if (!options?.length) {
    return [];
  }
  return options.map((o) => ({
    key: String(o.key ?? ''),
    value: String(o.value ?? '')
  }));
}

/** Row shape for local `p_profile_upload` cache after a successful upload (before SP refresh). */
export function buildProfileUploadRowForLocalCache(params: {
  cd_document_category: string;
  cd_document_type?: string;
  document_category?: string;
  document_type?: string;
  file_name: string;
  profile_tab_section?: string;
  status?: string;
  document_status?: string;
  document_id?: string | number | null;
}): Record<string, unknown> {
  const row: Record<string, unknown> = {
    cd_document_category: params.cd_document_category,
    cd_document_type: params.cd_document_type ?? '',
    document_category: params.document_category ?? params.cd_document_category,
    document_type: params.document_type ?? params.cd_document_type ?? '',
    file_name: params.file_name,
    profile_tab_section: params.profile_tab_section ?? '',
    status: params.status ?? 'Verified',
    is_active: true
  };
  if (params.document_status) {
    row.document_status = params.document_status;
  }
  if (params.document_id != null && String(params.document_id).trim() !== '') {
    row.document_id = Number(params.document_id);
    row.profile_document_upload_id = Number(params.document_id);
  }
  return row;
}

/** Append a newly uploaded document to in-memory dashboard row for duplicate checks before refresh. */
export function appendProfileUploadToDashboardRow(
  row: Record<string, unknown> | null | undefined,
  upload: Record<string, unknown>
): void {
  if (!row) {
    return;
  }
  const existing = profileUploadDocumentsFromDashboardRow(row);
  row['p_profile_upload'] = [...existing, upload];
}
